import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  getPulseErrorMessage,
  getPulsePayload,
  isPulseApiSuccess,
  listPulseDeck,
} from '../../services/pulseApi';
import {
  formatDeckPosition,
  formatPulseDate,
  isMatchRatingCard,
  PULSE_DECK_CYCLE,
  parseCardsPayload,
  statusChipColor,
} from './pulseUtils';

const PAGE_SIZE = 50;

function PulseDeck() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  const loadDeck = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await listPulseDeck({ limit: PAGE_SIZE, offset });
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to load active deck.'));
        setItems([]);
        setTotal(0);
        return;
      }
      const parsed = parseCardsPayload(getPulsePayload(result));
      setItems(parsed.items);
      setTotal(parsed.total);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load active deck.');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    loadDeck();
  }, [loadDeck]);

  const hasMore = offset + items.length < total;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">App deck</Typography>
        <IconButton onClick={loadDeck} disabled={loading} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Same push order as a fresh app open. Each cycle is <strong>{PULSE_DECK_CYCLE}</strong>, then
        repeat. The next admin hot take (newest upload first) starts the next cycle. One game is a
        prediction, Fan Wars, or rating. Linked story hot takes stay with their parent. Rows are in API
        order — do not re-sort. <code>deck_position</code> 1 = first card pushed. Nested hot take shows
        when the row is news or fan story.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell title="App push order. 1 = first card">#</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Hot take</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Expires</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No cards on the live deck.
                </TableCell>
              </TableRow>
            ) : (
              items.map((card) => {
                const ht = card.hot_take;
                return (
                  <TableRow
                    key={card.id}
                    hover
                    component={Link}
                    to={`/admin/pulse/cards/${encodeURIComponent(card.id)}`}
                    sx={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
                  >
                    <TableCell>
                      {formatDeckPosition(card.deck_position ?? card.deckPosition)}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={card.status} color={statusChipColor(card.status)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {card.card_type || card.category || '—'}
                      </Typography>
                      {isMatchRatingCard(card) && (
                        <Chip size="small" label="Rate Player" sx={{ mt: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {card.title || 'Untitled'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      {ht ? (
                        <>
                          <Typography variant="body2">{ht.statement || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(ht.options || []).map((o) => o.label).join(' · ') || 'no options'}
                            {ht.options_locked ? ' · locked' : ''}
                          </Typography>
                        </>
                      ) : card.has_hot_take ? (
                        <Chip size="small" label="linked" />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{formatPulseDate(card.created_at || card.createdAt)}</TableCell>
                    <TableCell>{formatPulseDate(card.expires_at ?? card.expiresAt)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Button
          size="small"
          disabled={offset <= 0 || loading}
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
        >
          Previous
        </Button>
        <Typography variant="body2" color="text.secondary">
          {total === 0 ? '0 cards' : `${offset + 1}–${offset + items.length} of ${total}`}
        </Typography>
        <Button size="small" disabled={!hasMore || loading} onClick={() => setOffset(offset + PAGE_SIZE)}>
          Next
        </Button>
      </Box>
    </Box>
  );
}

export default PulseDeck;
